import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { supabase } from './supabase';
import { 
  FiAlertCircle, 
  FiCheckCircle, 
  FiEye, 
  FiEyeOff, 
  FiSave,
  FiArrowLeft
} from 'react-icons/fi';
import { useI18n } from './i18n';
import './auth-pages.css';

function NewPasswordPage() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const { hasReauthenticated } = useAuth();
  const { t } = useI18n();

  // Redirect if the user hasn't reauthenticated
  useEffect(() => {
    if (!hasReauthenticated) {
      navigate('/dashboard/reauthenticate');
    }
  }, [hasReauthenticated, navigate]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const changePassword = async () => {
    // Reset messages
    setError('');
    setSuccess('');
    
    // Validate password
    if (newPassword.length < 8) {
      setError(t('auth.passwordLength'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    try {
      // Update password with Supabase
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      
      setSuccess(t('auth.passwordUpdated'));
      setTimeout(() => {
        navigate('/dashboard/profile');
      }, 2000);
    } catch (error) {
      setError(t('auth.passwordUpdateFailed', { message: error.message }));
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>{t('auth.changePassword')}</h1>
          <p>{t('auth.createSecurePassword')}</p>
        </div>

        {error && (
          <div className="auth-error">
            <FiAlertCircle />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="auth-success">
            <FiCheckCircle />
            <span>{success}</span>
          </div>
        )}

        <form className="auth-form">
          <div className="form-group">
            <div className="input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                className="form-control"
                placeholder={t('auth.newPassword')}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <button 
                type="button" 
                className="auth-btn link-btn" 
                onClick={togglePasswordVisibility}
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="form-control"
                placeholder={t('auth.confirmNewPassword')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <button 
                type="button" 
                className="auth-btn link-btn" 
                onClick={toggleConfirmPasswordVisibility}
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>

          <button type="button" className="auth-btn primary-btn" onClick={changePassword}>
            <FiSave /> {t('auth.updatePassword')}
          </button>
          
          <button type="button" className="auth-btn secondary-btn" onClick={() => navigate('/dashboard/profile')}>
            <FiArrowLeft /> {t('auth.backToProfile')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default NewPasswordPage;
